import {parsePhoneNumberFromString} from 'libphonenumber-js';
import React, {FC, useEffect, useMemo, useRef, useState} from 'react';
import styled from 'styled-components';

import {Trans} from '../../../../../libs/shared/core/src/helpers/internationalization/react';
import {isValidName} from '../../../../../libs/shared/core/src/helpers/utilities/stringHelpers';
import {TeammateModel} from '../../../../../libs/shared/core/src/models/api/teammateModel';
import {collectEnumValues} from '../../../../../libs/shared/core/src/models/api/types/enumTypes';
import {
  OnboardingEventsEnum,
  trackEvent,
  TrackingTypesEnum,
} from '../../../../../libs/shared/core/src/services/tracking/tracking';
import {TextBox} from '../../../../../libs/shared/ui/src/components/boxedInputs/textBox';
import {VisualSizesEnum} from '../../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../../libs/shared/ui/src/styles/fontStyles';
import {DepartmentsEnum} from '../../../../models/api/userMetadataModel';
import {GlobalPhoneNumberInput} from '../../../common/boxedInputs/phoneNumberInputBox';
import {
  CarouselNavigationButtonVariantsEnum,
  CarouselSlideButtonsContainer,
  CarouselSlideContentRight,
  CarouselSlideFooterRight,
} from '../../carouselCommon';
import {CarouselNavigationButton} from '../../carouselNavigationButton';
import {
  DepartmentsDropdownItems,
  WelcomeCarouselDropdownError,
  WelcomeCarouselDropdownPlaceholder,
} from '../welcomeCarouselCommon';
import {WelcomeCarouselDropdown} from './welcomeCarouselDropdown';
import {computeInitialDropdownSelection} from './welcomeCarouselFormHelpers';
import {PersonaFormOptions} from './welcomeCarouselPersonaFormContainer';

/*
 * Types.
 */

interface PhoneNumberInfo {
  phoneNumber: string;
  countryCode: string;
}

/*
 * Constants.
 */

enum PersonaFormValueTypesEnum {
  GIVEN_NAME = 'GIVEN_NAME',
  FAMILY_NAME = 'FAMILY_NAME',
  ALIAS = 'ALIAS',
  PHONE = 'PHONE',
  DEPARTMENT = 'DEPARTMENT',
}

/*
 * Props.
 */

interface WelcomeCarouselPersonaFormProps {
  user: TeammateModel;
  /** An optional department. We enforce DepartmentsEnum in our component, but not at the model level. */
  department?: string;
  /** Helper to get a few different types of alias error messages, or none. */
  getAliasErrorMessage: (alias: string) => string | null;
  /** Handler to go to previous slide in the carousel. */
  onPreviousClick: (() => void) | undefined;
  /** Handler for completing the persona form. */
  submitForm: (options: PersonaFormOptions) => void;
  /** Whether the user is the first global admin. */
  isUserFirstGlobalAdmin: boolean;
}

/*
 * Style.
 */

const StyledFormDiv = styled.div`
  display: grid;
  grid-template-rows: 1fr 90px 1fr auto;
  grid-row-gap: 10px;
`;

const StyledNameInputsDiv = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-column-gap: 12px;
`;

const StyledInputDescriptionDiv = styled.div`
  font-size: ${fontSizes.small};
  color: ${(p) => p.theme.greys.shade60};
  font-weight: ${fontWeights.medium};
  margin-top: -5px;
`;

/*
 * Component.
 */

export const WelcomeCarouselPersonaForm: FC<WelcomeCarouselPersonaFormProps> = (props) => {
  const {user, getAliasErrorMessage, onPreviousClick, submitForm, isUserFirstGlobalAdmin} = props;

  // Persona form values.
  const [givenName, setGivenName] = useState(user.givenName);
  const [familyName, setFamilyName] = useState(user.familyName);
  const [alias, setAlias] = useState(user.alias);
  const initialPhoneNumberInfo = computeInitialPhoneNumberInfo(user.phone);
  // Show the phone input only if the user did not have it pre-filled from
  // signup.
  // Once the experiment ends and if we decide to remove the phone input from
  // signup, we can choose to always show the phone input, since no one will
  // have a pre-filled phone number.
  const shouldShowPhoneInput = initialPhoneNumberInfo.phoneNumber.length === 0;
  const [phoneNumberInfo, setPhoneNumberInfo] = useState(initialPhoneNumberInfo);
  const [department, setDepartment] = useState<DepartmentsEnum | undefined>(
    computeInitialDropdownSelection(DepartmentsEnum, props.department),
  );

  // Form errors.
  const [formErrors, setFormErrors] = useState<Array<PersonaFormValueTypesEnum>>([]);

  // The value most recently modified that we need to validate.
  const [valueToValidate, setValueToValidate] = useState<PersonaFormValueTypesEnum | undefined>();

  // Map of value types to their respective validators.
  const validatorsForValueType = useMemo(
    () => ({
      [PersonaFormValueTypesEnum.GIVEN_NAME]: isValidName,
      [PersonaFormValueTypesEnum.FAMILY_NAME]: isValidName,
      [PersonaFormValueTypesEnum.ALIAS]: (a: any) => !getAliasErrorMessage(a), // Use `any` so that we can map through PersonaFormValueTypesEnum during validation.
      [PersonaFormValueTypesEnum.PHONE]: computeIsValidPhoneNumber,
      [PersonaFormValueTypesEnum.DEPARTMENT]: (d: DepartmentsEnum | undefined) => Boolean(d),
    }),
    [getAliasErrorMessage],
  );

  // Map of value types to their respective state variables.
  const stateForValueType = useMemo(
    () => ({
      [PersonaFormValueTypesEnum.GIVEN_NAME]: givenName,
      [PersonaFormValueTypesEnum.FAMILY_NAME]: familyName,
      [PersonaFormValueTypesEnum.ALIAS]: alias,
      [PersonaFormValueTypesEnum.PHONE]: phoneNumberInfo,
      [PersonaFormValueTypesEnum.DEPARTMENT]: department,
    }),
    [alias, department, familyName, givenName, phoneNumberInfo],
  );

  useEffect(() => {
    trackEvent(TrackingTypesEnum.ONBOARDING_STEP, OnboardingEventsEnum.STARTED_PERSONNA);
  }, []);

  // Initial render flag to prevent form validation on the first render of the persona form.
  // Otherwise all inputs would be in error states on the initial load.
  const isInitialPersonaFormRenderRef = useRef(true);

  useEffect(() => {
    // On the initial render of the persona form, do not validate.
    if (isInitialPersonaFormRenderRef.current) {
      isInitialPersonaFormRenderRef.current = false;
      return;
    }

    // If there is no value to validate, skip.
    if (!valueToValidate) {
      return;
    }

    const isValueValid = validatorsForValueType[valueToValidate](stateForValueType[valueToValidate]);
    const hasFormErrorForValue = formErrors.includes(valueToValidate);

    // If the value is valid, but an error exists, remove it.
    if (isValueValid && hasFormErrorForValue) {
      setFormErrors(formErrors.filter((e) => e !== valueToValidate));
    }

    // If the value is invalid, but an error does not exist, add it.
    if (!isValueValid && !hasFormErrorForValue) {
      setFormErrors(formErrors.concat(valueToValidate));
    }
  }, [
    alias,
    department,
    familyName,
    formErrors,
    givenName,
    phoneNumberInfo,
    validatorsForValueType,
    stateForValueType,
    valueToValidate,
  ]);

  const onSubmitForm = () => {
    let newFormErrors = formErrors.slice();

    // Only validate phone for the first global admin case.
    const formValues = isUserFirstGlobalAdmin
      ? collectEnumValues(PersonaFormValueTypesEnum)
      : collectEnumValues(PersonaFormValueTypesEnum).filter((v) => v !== PersonaFormValueTypesEnum.PHONE);

    formValues.forEach((valueType) => {
      const isValueValid = validatorsForValueType[valueType](stateForValueType[valueType]);
      const hasFormErrorForValue = formErrors.includes(valueType);

      // If the value is valid, but an error exists, remove it.
      if (isValueValid && hasFormErrorForValue) {
        newFormErrors = newFormErrors.filter((e) => e !== valueType);
      }

      // If the value is invalid, but an error does not exist, add it.
      if (!isValueValid && !hasFormErrorForValue) {
        newFormErrors = newFormErrors.concat(valueType);
      }
    });

    if (newFormErrors.length > 0) {
      setFormErrors(newFormErrors);
      return;
    }

    submitForm({
      givenName,
      familyName,
      alias,
      phone: formatPhoneNumberAsE164(phoneNumberInfo.phoneNumber),
      department,
    });
  };

  const aliasErrorMessage = getAliasErrorMessage(alias);

  const renderPersonaFormContent = () => (
    <>
      <StyledNameInputsDiv>
        <TextBox
          value={givenName}
          onChange={(value) => {
            setValueToValidate(PersonaFormValueTypesEnum.GIVEN_NAME);
            setGivenName(value);
          }}
          hasError={formErrors.includes(PersonaFormValueTypesEnum.GIVEN_NAME)}
          size={VisualSizesEnum.MEDIUM}
          placeholder=""
          label={<Trans id="welcomecarousel-personaform-givenname-label">First name</Trans>}
        />
        <TextBox
          value={familyName}
          onChange={(value) => {
            setValueToValidate(PersonaFormValueTypesEnum.FAMILY_NAME);
            setFamilyName(value);
          }}
          hasError={formErrors.includes(PersonaFormValueTypesEnum.FAMILY_NAME)}
          size={VisualSizesEnum.MEDIUM}
          placeholder=""
          label={<Trans id="welcomecarousel-personaform-familyname-label">Last name</Trans>}
        />
      </StyledNameInputsDiv>
      <div>
        <TextBox
          value={alias}
          onChange={(value) => {
            setValueToValidate(PersonaFormValueTypesEnum.ALIAS);
            setAlias(value);
          }}
          hasError={Boolean(aliasErrorMessage)}
          errorMessage={aliasErrorMessage}
          size={VisualSizesEnum.MEDIUM}
          placeholder=""
          label={<Trans id="welcomecarousel-personaform-alias-label">Username</Trans>}
        />
        {maybeRenderAliasDescription(aliasErrorMessage)}
      </div>
      {isUserFirstGlobalAdmin && shouldShowPhoneInput && (
        <GlobalPhoneNumberInput
          label={<Trans id="welcomecarousel-personaform-phonenumber-label">Phone number</Trans>}
          hasError={formErrors.includes(PersonaFormValueTypesEnum.PHONE)}
          phoneNumber={phoneNumberInfo.phoneNumber}
          phoneCountryCode={phoneNumberInfo.countryCode}
          onChange={(phoneNumber: string, country: any) => {
            setValueToValidate(PersonaFormValueTypesEnum.PHONE);
            setPhoneNumberInfo({
              phoneNumber,
              countryCode: (country && country.countryCode) || 'US',
            });
          }}
          tabIndex={0}
        />
      )}
      <div>
        <WelcomeCarouselDropdown<DepartmentsEnum>
          placeholder={
            <WelcomeCarouselDropdownPlaceholder>
              <Trans id="welcomecarousel-personaform-department-placeholder">Select role</Trans>
            </WelcomeCarouselDropdownPlaceholder>
          }
          selectedItemKey={department}
          items={DepartmentsDropdownItems}
          onSelect={(d) => {
            setValueToValidate(PersonaFormValueTypesEnum.DEPARTMENT);
            setDepartment(d.key);
          }}
          renderDescription={() => <Trans id="welcomecarousel-personaform-department-label">Role</Trans>}
        />
        <WelcomeCarouselDropdownError>
          {maybeRenderDropdownErrorText(formErrors, PersonaFormValueTypesEnum.DEPARTMENT)}
        </WelcomeCarouselDropdownError>
      </div>
    </>
  );

  return (
    <>
      <CarouselSlideContentRight>
        <StyledFormDiv>{renderPersonaFormContent()}</StyledFormDiv>
      </CarouselSlideContentRight>
      <CarouselSlideFooterRight>
        <CarouselSlideButtonsContainer>
          {onPreviousClick && (
            <CarouselNavigationButton
              variant={CarouselNavigationButtonVariantsEnum.SUBDUED_LINK}
              onClick={onPreviousClick}
            >
              <Trans id="welcomecarousel-companyform-previous">Previous</Trans>
            </CarouselNavigationButton>
          )}
          <CarouselNavigationButton
            variant={CarouselNavigationButtonVariantsEnum.PRIMARY}
            onClick={onSubmitForm}
          >
            {renderNavigationButtonText(isUserFirstGlobalAdmin)}
          </CarouselNavigationButton>
        </CarouselSlideButtonsContainer>
      </CarouselSlideFooterRight>
    </>
  );
};

/*
 * Helpers.
 */

function maybeRenderAliasDescription(aliasErrorMessage: string | null) {
  if (aliasErrorMessage) {
    return null;
  }

  return (
    <StyledInputDescriptionDiv>
      <Trans id="welcomecarousel-personaform-alias-description">
        How your teammates will <span>@mention</span> you in Front.
      </Trans>
    </StyledInputDescriptionDiv>
  );
}

function maybeRenderDropdownErrorText(
  formErrors: Array<PersonaFormValueTypesEnum>,
  dropdownType: PersonaFormValueTypesEnum,
) {
  if (!formErrors.includes(dropdownType)) {
    return null;
  }

  switch (dropdownType) {
    case PersonaFormValueTypesEnum.DEPARTMENT:
      return <Trans id="welcomecarousel-personaform-department-error">Please select a department</Trans>;

    default:
      return null;
  }
}

function renderNavigationButtonText(isUserFirstGlobalAdmin: boolean) {
  // If the user is not the first global admin, the persona form slide will be the only one in the carousel.
  if (!isUserFirstGlobalAdmin) {
    return <Trans id="welcomecarousel-personaform-done">Explore Front</Trans>;
  }

  return <Trans id="welcomecarousel-personaform-next">Next</Trans>;
}

/**
 * Computes the initial phone number info for the form. Utilized when the user
 * goes back to this form page from a later page.
 */
function computeInitialPhoneNumberInfo(phoneNumber: string) {
  // Prefix with '+' so it tries to parse the string as an international phone
  // number.
  const phoneNumberToParse = formatPhoneNumberAsE164(phoneNumber);
  const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumberToParse);
  if (parsedPhoneNumber) {
    return {
      // libphonenumber returns the number in E.164 format, but the phone number
      // input requires just the bare number.
      phoneNumber: parsedPhoneNumber.number.replace('+', ''),
      countryCode: parsedPhoneNumber.country || 'US',
    };
  }

  return {
    phoneNumber: '',
    countryCode: 'US',
  };
}

/**
 * Checks whether the phone number is valid for the given country.
 */
function computeIsValidPhoneNumber(phoneNumberInfo: PhoneNumberInfo) {
  const {phoneNumber, countryCode} = phoneNumberInfo;
  const parsedPhoneNumber = parsePhoneNumberFromString(phoneNumber, countryCode.toUpperCase() as any);

  // There is an `isValid()` method on the PhoneNumber class, but the library
  // author recommends we not use it because validation rules change quite
  // often. Like the website, we just make sure we can parse out a phone number.
  return Boolean(parsedPhoneNumber);
}

/**
 * Ensures the passed-in phone number looks roughly like an E.164 formatted
 * phone number by making sure it's prefixed with '+'.
 *
 * E.164 is a formatting standard that ensures each conformant phone number is
 * globally unique. It is Twilio's preferred format for serializing phone
 * numbers.
 *
 * https://support.twilio.com/hc/en-us/articles/223183008-Formatting-International-Phone-Numbers
 */
function formatPhoneNumberAsE164(phoneNumber: string) {
  return phoneNumber.startsWith('+') ? phoneNumber : `+${phoneNumber}`;
}
